import { isEmpty, last, orderBy } from 'lodash';

import { Nullable } from 'shared/types';
import {
  Media,
  mediaStatusId,
  mediaTypeId,
  mediaVisibilityId,
} from 'utils/media/mediaTypes';
import { userSession } from 'utils/user/userSession';
import { environmentVariables } from 'utils/environmentVariables';

const MEDIA_BUCKET_URL = `${environmentVariables.REACT_APP_MEDIA_BUCKET_URL}/`;

export const isMediaPhoto = (media: Media): boolean => {
  return media?.mediaTypeId === mediaTypeId.photo;
};

export const isMediaVideo = (media: Media): boolean => {
  return media?.mediaTypeId === mediaTypeId.video;
};

export const isMediaPedigreeFile = (media: Media): boolean => {
  return media.mediaTypeId === mediaTypeId.pedigreeFile;
};

export const isMediaPendingReview = (media: Media): boolean => {
  return media?.mediaStatusId === mediaStatusId.pendingReview;
};

export const isMediaApproved = (m: Media) => {
  return m.mediaStatusId === mediaStatusId.approved;
};

export const getMediaVariant = (
  media: Media,
  variant: number
): Nullable<string> => {
  return media?.mediaVariants?.length > 0
    ? media?.mediaVariants?.filter((item) => item.photoSizeId === variant)[0]
        .filePath
    : null;
};

export const getMediaBucketUrl = (suffix: string): Nullable<string> => {
  return suffix ? `${MEDIA_BUCKET_URL}${suffix}` : null;
};

export const getOrderedImages = (photos: Media[]): Media[] => {
  let orderedImages = photos.sort(
    (a, b) =>
      new Date(b.uploadedAt).valueOf() - new Date(a.uploadedAt).valueOf()
  );

  orderedImages = orderedImages.map((item, index) => {
    if (index !== 0) {
      return {
        ...item,
        checked: true,
        visibilityId: mediaVisibilityId.hidden,
      };
    }
    return item;
  });

  return orderedImages;
};

export const getCheckedImages = (media: Media[]): Media[] => {
  return media.filter((image) => image.checked);
};

export const getSortedImagesByViewOrder = (data: Media[]): Media[] => {
  const images = getImages(data);
  const sorted = [...images];
  sorted.sort((a, b) => (a.viewOrder > b.viewOrder ? 1 : -1));
  return sorted;
};

export const getCoverImages = (media: Media[]): Media[] => {
  return media.filter((m) => isMediaPublic(m) && isMediaPhoto(m));
};

export const getHiddenImages = (media: Media[]): Media[] => {
  return media.filter(
    (m) => !m?.mediaVisibilityDeferral && isMediaHidden(m) && isMediaPhoto(m)
  );
};

export const getAdditionalImages = (media: Media[]): Media[] =>
  media.filter(
    (m) => m.visibilityId === mediaVisibilityId.private && isMediaPhoto(m)
  );

export const getRestOfImages = (media: Media[]): Media[] => {
  return media.filter(
    (m) =>
      !isMediaPublic(m) &&
      !isMediaPrivate(m) &&
      !isMediaHidden(m) &&
      isMediaPhoto(m)
  );
};

export const getOtherMedia = (media: Media[]): Media[] => {
  return media.filter((m) => !isMediaPhoto(m));
};

export const getImages = (media: Media[]): Media[] => {
  return media.filter((item) => isMediaPhoto(item));
};

export const getDeletedMedia = (media: Media[]): Media[] => {
  return media.filter((m) => m?.mediaVisibilityDeferral);
};

export const getOrderedImageArray = (
  media: Media[],
  visibility: number
): Media[] => {
  const images = media.filter(
    (m) => isMediaPhoto(m) && m.visibilityId === visibility
  );

  images.sort((a, b) => (a.viewOrder > b.viewOrder ? 1 : -1));
  return images;
};

export const getMainParentPhoto = (media: Media[]): Media => {
  const photosMedia = media.filter(
    (m) =>
      isMediaPhoto(m) &&
      !isEmpty(m.uploadedAt) &&
      !isMediaDeleted(m) &&
      (isMediaApproved(m) ||
        isMediaPendingReview(m) ||
        isMediaStatusPublished(m))
  );
  const sortedPhotos = orderBy(photosMedia, ['uploadedAt'], ['desc']);
  return sortedPhotos[0];
};

export const isMediaStatusPublished = (media: Media): boolean => {
  return media.mediaStatusId === mediaStatusId.published;
};

export const isMediaUploadedByCurrentUser = (media: Media): boolean => {
  return media.uploadedByUserId === userSession()?.id;
};

export const isMediaPublic = (media: Media): boolean => {
  return media.visibilityId === mediaVisibilityId.public;
};

export const isMediaHidden = (media: Media): boolean => {
  return media.visibilityId === mediaVisibilityId.hidden;
};

export const isMediaPrivate = (media: Media): boolean => {
  return media.visibilityId === mediaVisibilityId.private;
};

export const isMediaDeleted = (m: Media) => {
  return !isEmpty(m?.mediaVisibilityDeferral);
};

export const getMediaPedigreeFile = (media: Media[]) => {
  const pedigreeFiles = media.filter(
    (m) => isMediaPedigreeFile(m) && isMediaPublic(m)
  );

  pedigreeFiles.sort(
    (a, b) =>
      new Date(a.uploadedAt).valueOf() - new Date(b.uploadedAt).valueOf()
  );
  return last(pedigreeFiles);
};

export const getVideoCloudflareAttributes = (media: Media) => {
  return media.attributes?.find((attr) => attr.name === 'Cloudflare Uid');
};
